import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Crosshair,
  Zap,
  Send,
  AlertTriangle,
  CheckCircle,
  Clock,
  Terminal,
  Cpu,
  Loader2,
  Puzzle,
  Trash2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const PROVIDERS = [
  { value: 'router', label: 'Auto (best model)' },
  { value: 'openai', label: 'GPT-5.2' },
  { value: 'anthropic', label: 'Claude Sonnet 4.5' },
  { value: 'gemini', label: 'Gemini 3 Flash' },
];

// Faint radar sweep drawn behind the chat, purely decorative
const RadarSweep = ({ agents }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) - 10;
    let angle = 0;
    let frame;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.strokeStyle = 'rgba(0, 255, 65, 0.5)';
      ctx.lineWidth = 1;
      for (let i = 1; i <= 4; i++) {
        ctx.beginPath();
        ctx.arc(centerX, centerY, (radius / 4) * i, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.moveTo(centerX - radius, centerY);
      ctx.lineTo(centerX + radius, centerY);
      ctx.moveTo(centerX, centerY - radius);
      ctx.lineTo(centerX, centerY + radius);
      ctx.stroke();

      // Sweep wedge
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
      gradient.addColorStop(0, 'rgba(0, 255, 65, 0.35)');
      gradient.addColorStop(1, 'rgba(0, 255, 65, 0.05)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, angle - 0.6, angle);
      ctx.closePath();
      ctx.fill();

      // Agent blips
      agents.forEach((agent, i) => {
        const agentAngle = (i / Math.max(agents.length, 1)) * Math.PI * 2;
        const agentRadius = radius * (0.3 + (i % 3) * 0.2);
        ctx.fillStyle = agent.status === 'active' ? '#00FF41' : agent.status === 'paused' ? '#FFB800' : '#FF003C';
        ctx.beginPath();
        ctx.arc(centerX + Math.cos(agentAngle) * agentRadius, centerY + Math.sin(agentAngle) * agentRadius, 6, 0, Math.PI * 2);
        ctx.fill();
      });

      angle = (angle + 0.008) % (Math.PI * 2);
      frame = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(frame);
  }, [agents]);

  return (
    <canvas
      ref={canvasRef}
      width={700}
      height={700}
      aria-hidden="true"
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(90%,700px)] aspect-square opacity-[0.08] pointer-events-none"
    />
  );
};

// AI link status pill
const LinkStatus = ({ status }) => {
  const statusColors = {
    online: { bg: 'bg-primary/20', text: 'text-primary', dot: 'bg-primary' },
    degraded: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', dot: 'bg-yellow-400' },
    offline: { bg: 'bg-red-500/20', text: 'text-red-400', dot: 'bg-red-400' },
    checking: { bg: 'bg-gray-500/20', text: 'text-gray-400', dot: 'bg-gray-400' }
  };
  const colors = statusColors[status.status] || statusColors.checking;

  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded ${colors.bg}`} data-testid="link-status">
      <span className={`w-2.5 h-2.5 rounded-full ${colors.dot} ${status.status === 'online' ? 'animate-pulse' : ''}`} />
      <span className={`text-sm font-mono uppercase tracking-wider ${colors.text}`}>
        AI {status.status}
      </span>
      {status.latency_ms && (
        <span className="text-sm font-mono text-primary/50">{Math.round(status.latency_ms)}ms</span>
      )}
    </div>
  );
};

// Mission Control Component
const MissionControl = ({ agents, stats, skills = [], initialSkillId = '' }) => {
  const [linkStatus, setLinkStatus] = useState({ status: 'checking' });
  const [taskInput, setTaskInput] = useState('');
  const [taskResult, setTaskResult] = useState(null);
  const [taskLoading, setTaskLoading] = useState(false);
  const [recentTasks, setRecentTasks] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [provider, setProvider] = useState(process.env.REACT_APP_MISSION_PROVIDER || 'router');
  const [skillId, setSkillId] = useState(initialSkillId);
  const chatEndRef = useRef(null);

  const usableSkills = skills.filter((s) => s.has_instructions);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/openclaw/status`);
        setLinkStatus(response.data);
      } catch (error) {
        setLinkStatus({ status: 'offline', error: error.message });
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const response = await axios.get(`${API}/openclaw/tasks`, { withCredentials: true });
        setRecentTasks(response.data.slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch tasks:', error);
      }
    };
    fetchTasks();
  }, [taskResult]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatLoading]);

  const executeTask = async () => {
    if (!taskInput.trim() || taskLoading) return;

    setTaskLoading(true);
    setTaskResult(null);

    try {
      const response = await axios.post(
        `${API}/openclaw/execute`,
        { task: taskInput },
        { withCredentials: true }
      );
      setTaskResult(response.data);
      setTaskInput('');
    } catch (error) {
      setTaskResult({
        status: 'error',
        result: error.response?.data?.detail || error.message
      });
    } finally {
      setTaskLoading(false);
    }
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatLoading) return;

    const history = chatMessages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map(({ role, content }) => ({ role, content }));
    setChatMessages((prev) => [...prev, { role: 'user', content: text }]);
    setChatInput('');
    setChatLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat`,
        { message: text, provider, skill_id: skillId || null, history },
        { withCredentials: true }
      );
      const skillName = usableSkills.find((s) => s.skill_id === skillId)?.name;
      setChatMessages((prev) => [...prev, {
        role: 'assistant', content: response.data.response, model: response.data.model, skill: skillName
      }]);
    } catch (error) {
      setChatMessages((prev) => [...prev, { role: 'error', content: error.response?.data?.detail || error.message }]);
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 xl:h-[calc(100vh-8.5rem)]">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="font-military text-2xl text-primary flex items-center gap-3">
          <Crosshair className="w-7 h-7" />
          MISSION CONTROL
        </h2>
        <LinkStatus status={linkStatus} />
      </div>

      <div className="flex-1 min-h-0 grid xl:grid-cols-[1fr_340px] gap-4">
        {/* Chat: takes most of the screen */}
        <div className="cyber-card relative overflow-hidden border border-primary/20 flex flex-col min-h-[70vh] xl:min-h-0">
          <RadarSweep agents={agents} />

          {/* Chat toolbar */}
          <div className="relative flex flex-wrap items-center gap-3 p-4 border-b border-primary/10">
            <Terminal className="w-5 h-5 text-primary" />
            <span className="font-orbitron text-sm font-bold text-primary mr-auto">NEURAL LINK</span>

            <label className="flex items-center gap-2 text-sm font-mono text-primary/60">
              MODEL
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                className="cyber-input text-sm py-1.5"
                data-testid="mission-provider"
              >
                {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </label>

            <label className="flex items-center gap-2 text-sm font-mono text-primary/60">
              <Puzzle className="w-4 h-4" />
              SKILL
              <select
                value={skillId}
                onChange={(e) => setSkillId(e.target.value)}
                className="cyber-input text-sm py-1.5 max-w-[16rem]"
                data-testid="mission-skill"
              >
                <option value="">None</option>
                {usableSkills.map((s) => <option key={s.skill_id} value={s.skill_id}>{s.name}</option>)}
              </select>
            </label>

            {chatMessages.length > 0 && (
              <button
                onClick={() => setChatMessages([])}
                className="flex items-center gap-1 text-sm font-mono text-primary/50 hover:text-red-400"
                data-testid="mission-clear"
              >
                <Trash2 className="w-4 h-4" /> CLEAR
              </button>
            )}
          </div>

          {/* Messages */}
          <div className="relative flex-1 min-h-0 overflow-y-auto p-6 space-y-4" data-testid="openclaw-chat">
            {chatMessages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-primary/40">
                <Cpu className="w-16 h-16 mb-3" />
                <p className="font-mono text-base">NEURAL LINK READY</p>
                <p className="font-mono text-sm mt-2 text-primary/30">Pick a skill above to put it to work, or just ask.</p>
              </div>
            )}
            {chatMessages.map((msg, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded font-mono text-base leading-relaxed max-w-[85%] ${
                  msg.role === 'user'
                    ? 'bg-primary/15 text-primary ml-auto'
                    : msg.role === 'error'
                    ? 'bg-red-500/10 text-red-400 border border-red-500/30'
                    : 'bg-black/70 text-gray-200 border border-primary/15'
                }`}
              >
                {msg.role === 'assistant' && (
                  <p className="text-xs text-primary/50 mb-2 uppercase">
                    {msg.model}{msg.skill ? ` · skill: ${msg.skill}` : ''}
                  </p>
                )}
                {msg.role === 'error' && <p className="text-xs mb-2">ERROR</p>}
                <p className="whitespace-pre-wrap break-words">{msg.content}</p>
              </motion.div>
            ))}
            {chatLoading && (
              <div className="flex items-center gap-2 text-primary/60 p-3">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm font-mono">PROCESSING...</span>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div className="relative p-4 border-t border-primary/10 flex gap-3 items-end">
            <textarea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendChat();
                }
              }}
              rows={3}
              placeholder="Type a message... (Enter to send, Shift+Enter for a new line)"
              className="cyber-input flex-1 text-base resize-none"
              data-testid="openclaw-chat-input"
            />
            <button
              onClick={sendChat}
              disabled={chatLoading || !chatInput.trim()}
              className="cyber-btn px-6 py-3"
              data-testid="openclaw-chat-send"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Side column: stats + tasks */}
        <div className="flex flex-col gap-4 min-h-0 xl:overflow-y-auto">
          <div className="cyber-card p-4 border border-primary/20 grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-2xl font-orbitron text-primary">{stats.active_agents}</p>
              <p className="text-xs font-mono text-primary/50">ACTIVE</p>
            </div>
            <div>
              <p className="text-2xl font-orbitron text-yellow-400">{stats.total_agents - stats.active_agents}</p>
              <p className="text-xs font-mono text-primary/50">STANDBY</p>
            </div>
            <div>
              <p className="text-2xl font-orbitron text-primary">{usableSkills.length}</p>
              <p className="text-xs font-mono text-primary/50">SKILLS</p>
            </div>
          </div>

          <div className="cyber-card p-4 border border-primary/20">
            <h3 className="font-orbitron text-sm font-bold mb-3 flex items-center gap-2 text-primary">
              <Zap className="w-4 h-4" />
              TASK EXECUTION
            </h3>

            <textarea
              value={taskInput}
              onChange={(e) => setTaskInput(e.target.value)}
              placeholder="Describe a task to run..."
              className="cyber-input w-full h-24 resize-none text-sm"
              data-testid="openclaw-task-input"
            />
            <button
              onClick={executeTask}
              disabled={taskLoading || !taskInput.trim()}
              className="cyber-btn w-full mt-3 flex items-center justify-center gap-2"
              data-testid="openclaw-task-execute"
            >
              {taskLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> EXECUTING...</>
              ) : (
                <><Zap className="w-4 h-4" /> EXECUTE TASK</>
              )}
            </button>

            <AnimatePresence>
              {taskResult && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className={`mt-4 p-3 rounded border ${
                    taskResult.status === 'completed'
                      ? 'bg-primary/5 border-primary/30'
                      : 'bg-red-500/5 border-red-500/30'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {taskResult.status === 'completed' ? (
                      <CheckCircle className="w-4 h-4 text-primary" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                    )}
                    <span className="text-sm font-mono uppercase">
                      {taskResult.status === 'completed' ? 'TASK COMPLETE' : 'EXECUTION ERROR'}
                    </span>
                  </div>
                  <p className="text-sm font-mono text-gray-300 max-h-64 overflow-y-auto whitespace-pre-wrap">
                    {taskResult.result}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {recentTasks.length > 0 && (
              <div className="mt-4 pt-4 border-t border-primary/10">
                <p className="text-xs font-mono text-primary/50 mb-2">RECENT OPERATIONS</p>
                <div className="space-y-2">
                  {recentTasks.map((task, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm font-mono">
                      <Clock className="w-3 h-3 text-primary/30" />
                      <span className="text-gray-400 truncate flex-1">{task.task}</span>
                      <span className={task.status === 'completed' ? 'text-primary' : 'text-red-400'}>
                        {task.status === 'completed' ? '✓' : '✗'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionControl;
